/**
 * Gift Message Custom Element
 * Handles gift message functionality with toggle and form management
 */
class GiftMessage extends HTMLElement {
  constructor() {
    super();

    this.sectionId = this.dataset.sectionId;
    this.debounceTimeout = 500;
    // Longer debounce for the screen-reader announcement than for the cart update: it must
    // fire only after the user has GENUINELY paused (not between words), so the count and the
    // field re-read finish without being cut off when typing resumes.
    this.announceDebounce = 1200;
    this.maxCharacters = 80;
    this.allowedPattern = /^[a-zA-Z0-9\s.,!?'"()-]*$/;

    this.checkbox = null;
    this.form = null;
    this.toInput = null;
    this.fromInput = null;
    this.noteTextarea = null;
    this.debouncedUpdate = null;
  }

  connectedCallback() {
    this.initializeElements();
    this.setupEventListeners();
    this.loadExistingData();
  }

  initializeElements() {
    this.checkbox = this.querySelector(`#gift-message-toggle-${this.sectionId}`);
    this.form = this.querySelector(`#gift-message-form-${this.sectionId}`);
    this.toInput = this.querySelector(`#gift-message-to-${this.sectionId}`);
    this.fromInput = this.querySelector(`#gift-message-from-${this.sectionId}`);
    this.noteTextarea = this.querySelector(`#gift-message-note-${this.sectionId}`);

    if (!this.checkbox || !this.form || !this.toInput || !this.fromInput || !this.noteTextarea) {
      console.error('GiftMessage: Required elements not found');
      return;
    }

    this.noteTextarea.setAttribute('maxlength', this.maxCharacters);
    // Counter and live region are rendered in gift-message.liquid so they exist in the
    // DOM (and the a11y tree) before any text updates — required for the live region to
    // announce. See WCAG 4.1.3.
    this.noteCounter = this.querySelector(`#gift-message-note-counter-${this.sectionId}`);
    this.noteStatus = this.querySelector(`#gift-message-note-status-${this.sectionId}`);
    this.updateCharacterCounter(this.noteTextarea, this.noteCounter);

    this.debouncedUpdate = FoxTheme.utils.debounce(this.handleFormUpdate.bind(this), this.debounceTimeout);
    // Debounce screen-reader announcements separately from the instant visual update so
    // the count isn't read out on every keystroke.
    this.debouncedAnnounce = FoxTheme.utils.debounce(this.announceCharacterCount.bind(this), this.announceDebounce);
  }

  updateCharacterCounter(inputElement, counterElement) {
    if (!inputElement || !counterElement) return;

    const currentLength = inputElement.value.length;
    counterElement.textContent = `${currentLength}/${this.maxCharacters} No special characters.`;

    if (currentLength >= this.maxCharacters) {
      counterElement.style.color = '#dc3545';
    } else if (currentLength >= this.maxCharacters * 0.9) {
      counterElement.style.color = '#ffc107';
    } else {
      counterElement.style.color = '#666';
    }
  }

  announceCharacterCount() {
    if (!this.noteTextarea || !this.noteStatus) return;

    // The live region lives outside the form, so it stays live even while the form is hidden.
    // Skip (and clear) when the gift message is toggled off, so a debounced call that lands
    // after the user clears the form doesn't announce a stray count.
    if (this.checkbox && !this.checkbox.checked) {
      this.noteStatus.textContent = '';
      return;
    }

    const currentLength = this.noteTextarea.value.length;
    const remaining = this.maxCharacters - currentLength;

    // Announce the remaining count on each typing PAUSE (the input handler debounces this).
    // The region is aria-live="polite", so it waits for the screen reader's keystroke echo
    // to finish rather than interrupting it — the earlier cut-off was caused by a dynamic
    // aria-describedby forcing a full field re-read, which is now a static hint. (WCAG 4.1.3)
    let message;
    if (remaining <= 0) {
      message = `Character limit reached. ${this.maxCharacters} of ${this.maxCharacters} characters used.`;
    } else {
      message = `${remaining} character${remaining === 1 ? '' : 's'} remaining.`;
    }

    // Only write when the text actually changes; aria-atomic re-reads the whole region on
    // any mutation, so an identical re-write would announce again for nothing.
    if (message !== this.noteStatus.textContent) {
      this.noteStatus.textContent = message;
    }
  }

  sanitizeInput(value) {
    return value.replace(/[^\w\s.,!?'"()-]/g, '').replace(/[\u{1F600}-\u{1F6FF}|\u{1F300}-\u{1F5FF}|\u{1F680}-\u{1F6FF}|\u{1F1E0}-\u{1F1FF}|\u{2600}-\u{26FF}|\u{2700}-\u{27BF}|\u{FE00}-\u{FE0F}|\u{1F900}-\u{1F9FF}|\u{1FA00}-\u{1FA6F}|\u{1FA70}-\u{1FAFF}|\u{231A}-\u{231B}|\u{23E9}-\u{23F3}|\u{23F8}-\u{23FA}|\u{25AA}-\u{25AB}|\u{25B6}|\u{25C0}|\u{25FB}-\u{25FE}|\u{2614}-\u{2615}|\u{2648}-\u{2653}|\u{267F}|\u{2693}|\u{26A1}|\u{26AA}-\u{26AB}|\u{26BD}-\u{26BE}|\u{26C4}-\u{26C5}|\u{26CE}|\u{26D4}|\u{26EA}|\u{26F2}-\u{26F3}|\u{26F5}|\u{26FA}|\u{26FD}|\u{2702}|\u{2705}|\u{2708}-\u{270D}|\u{270F}|\u{2712}|\u{2714}|\u{2716}|\u{271D}|\u{2721}|\u{2728}|\u{2733}-\u{2734}|\u{2744}|\u{2747}|\u{274C}|\u{274E}|\u{2753}-\u{2755}|\u{2757}|\u{2763}-\u{2764}|\u{2795}-\u{2797}|\u{27A1}|\u{27B0}|\u{27BF}|\u{2934}-\u{2935}|\u{2B05}-\u{2B07}|\u{2B1B}-\u{2B1C}|\u{2B50}|\u{2B55}|\u{3030}|\u{303D}|\u{3297}|\u{3299}]/gu, '');
  }

  handleInputWithSanitization(inputElement, counterElement) {
    const originalValue = inputElement.value;
    const sanitizedValue = this.sanitizeInput(originalValue);

    if (originalValue !== sanitizedValue) {
      inputElement.value = sanitizedValue;
    }

    this.updateCharacterCounter(inputElement, counterElement);
  }

  setupEventListeners() {
    if (!this.checkbox || !this.debouncedUpdate) return;

    this.checkbox.addEventListener('change', this.handleToggleChange.bind(this));

    if (this.toInput) {
      this.toInput.addEventListener('input', () => {
        this.debouncedUpdate();
      });
    }
    if (this.fromInput) {
      this.fromInput.addEventListener('input', () => {
        this.debouncedUpdate();
      });
    }
    if (this.noteTextarea) {
      this.noteTextarea.addEventListener('input', () => {
        this.handleInputWithSanitization(this.noteTextarea, this.noteCounter);
        this.debouncedAnnounce();
        this.debouncedUpdate();
      });
    }
  }

  loadExistingData() {
    if (this.hasExistingData()) {
      this.checkbox.checked = true;
      this.showForm();
    }
  }

  hasExistingData() {
    return (
      (this.toInput && this.toInput.value.trim() !== '') ||
      (this.fromInput && this.fromInput.value.trim() !== '') ||
      (this.noteTextarea && this.noteTextarea.value.trim() !== '')
    );
  }

  handleToggleChange(event) {
    if (event.target.checked) {
      this.showForm();
    } else {
      this.hideForm();
      this.clearFormData();
    }
  }

  showForm() {
    if (this.form) {
      this.form.style.display = '';
      this.form.setAttribute('aria-hidden', 'false');
    }
  }

  hideForm() {
    if (this.form) {
      this.form.style.display = 'none';
      this.form.setAttribute('aria-hidden', 'true');
    }
  }

  clearFormData() {
    if (this.toInput) {
      this.toInput.value = '';
      this.toInput.classList.remove('field-error');
    }
    if (this.fromInput) {
      this.fromInput.value = '';
      this.fromInput.classList.remove('field-error');
    }
    if (this.noteTextarea) {
      this.noteTextarea.value = '';
      this.noteTextarea.classList.remove('field-error');
      this.updateCharacterCounter(this.noteTextarea, this.noteCounter);
    }
    if (this.noteStatus) {
      this.noteStatus.textContent = '';
    }

    this.updateCartAttributes({
      'to': '',
      'from': '',
      'note': ''
    });
  }

  areAllFieldsFilled() {
    return (
      this.toInput && this.toInput.value.trim() !== '' &&
      this.fromInput && this.fromInput.value.trim() !== '' &&
      this.noteTextarea && this.noteTextarea.value.trim() !== ''
    );
  }

  /**
   * Validate all required fields using HTML5 validation
   */
  validateRequiredFields() {
    if (!this.form) return true;

    const requiredFields = [this.toInput, this.fromInput, this.noteTextarea];
    let isValid = true;

    requiredFields.forEach(field => {
      if (field && !field.checkValidity()) {
        isValid = false;
        // Add visual feedback for invalid fields
        field.classList.add('field-error');
      } else if (field) {
        // Remove error styling for valid fields
        field.classList.remove('field-error');
      }
    });

    return isValid;
  }

  /**
   * Collect form data
   */
  collectFormData() {
    return {
      'to': this.toInput ? this.toInput.value.trim() : '',
      'from': this.fromInput ? this.fromInput.value.trim() : '',
      'note': this.noteTextarea ? this.noteTextarea.value.trim() : ''
    };
  }

  handleFormUpdate() {
    if (!this.checkbox.checked) return;

    // Use HTML5 validation to check if all required fields are valid
    if (this.validateRequiredFields() && this.areAllFieldsFilled()) {
      const formData = this.collectFormData();
      this.updateCartAttributes(formData);
    }
  }

  updateCartAttributes(data) {
    const requestBody = JSON.stringify({
      note: `to: ${data.to}, from: ${data.from}, message: ${data.note}`,
    });

    fetch(`${FoxTheme.routes.cart_update_url}`, {
      ...FoxTheme.utils.fetchConfig(),
      body: requestBody
    })
    .then(response => {
      if (!response.ok) {
        throw new Error('Network response was not ok');
      }
      return response.json();
    })
    .catch(error => {
      console.error('Error updating gift message:', error);
    });
  }
}

customElements.define('gift-message', GiftMessage);
